import {
    Box,
    VStack,
    Text,
    Input,
    Button,
    Image,
    InputGroup,
    PinInput,
} from "@chakra-ui/react";

import {
    useState,
} from "react";

import {
    Eye,
    EyeOff,
} from "lucide-react";

import {
    useNavigate,
    useLocation,
} from "react-router-dom";

import {
    useDispatch,
    useSelector,
} from "react-redux";

import Logo from "../../../../assets/logo.png";
import routes from "../../../../routes";

import {
    confirmPasswordResetAction,
} from "../../../../state/actions/authActions";

import {
    toaster,
} from "../../../../components/toaster";

import ActivityToast from "../../../../components/ActivityToast";

const ResetPasswordConfirmForm = () => {
    const navigate =
        useNavigate();

    const location =
        useLocation();

    const dispatch =
        useDispatch();

    const {
        loading,
    } = useSelector(
        (state) =>
            state.auth
    );

    const {
        user_id,
        email,
    } =
        location.state || {};

    const [
        otp,
        setOtp,
    ] = useState("");

    const [
        newPassword,
        setNewPassword,
    ] = useState("");

    const [
        confirmPassword,
        setConfirmPassword,
    ] = useState("");

    const [
        showPassword,
        setShowPassword,
    ] = useState(false);

    const [
        showConfirmPassword,
        setShowConfirmPassword,
    ] = useState(false);

    const passwordRequirements = {
        minLength:
            newPassword.length >= 10,

        uppercase:
            /[A-Z]/.test(
                newPassword
            ),

        lowercase:
            /[a-z]/.test(
                newPassword
            ),

        number:
            /[0-9]/.test(
                newPassword
            ),

        special:
            /[^A-Za-z0-9]/.test(
                newPassword
            ),
    };

    const isPasswordValid =
        passwordRequirements.minLength &&
        passwordRequirements.uppercase &&
        passwordRequirements.lowercase &&
        passwordRequirements.number &&
        passwordRequirements.special;

    const passwordsMatch =
        newPassword ===
        confirmPassword;

    const isValid =
        otp.length === 6 &&
        isPasswordValid &&
        passwordsMatch;

    const handleSubmit =
        async () => {
            if (!isValid) {
                return;
            }

            const result =
                await dispatch(
                    confirmPasswordResetAction(
                        {
                            user_id,
                            otp:
                                Number(
                                    otp
                                ),
                            new_password:
                                newPassword,
                            password_confirm:
                                confirmPassword,
                        }
                    )
                );

            if (
                result?.success
            ) {
                toaster.create({
                    render: () => (
                        <ActivityToast
                            type="success"
                            title="Password Updated"
                            description="Your password has been reset successfully."
                            timestamp="Just now"
                        />
                    ),
                });

                setTimeout(
                    () => {
                        navigate(
                            routes.login
                        );
                    },
                    1000
                );
            } else {
                toaster.create({
                    render: () => (
                        <ActivityToast
                            type="error"
                            title="Reset Failed"
                            description={
                                result?.error ||
                                "Unable to reset password."
                            }
                            timestamp="Just now"
                        />
                    ),
                });
            }
        };

    return (
        <Box
            p={{
                base: 6,
                md: 10,
                lg: 20,
            }}
                minH="100vh"
    overflowY="auto"

        >
            <Box
                textAlign="center"
            >
                <Image
                    src={Logo}
                    h="48px"
                    mb={12}
                    mx="auto"
                    cursor="pointer"
                    onClick={() =>
                        navigate(
                            routes.home
                        )
                    }
                />

                <Text
                    fontSize={{
                        base: "32px",
                        md: "48px",
                    }}
                    fontWeight="700"
                    color="#071D54"
                    mb={2}
                >
                    Reset Password
                </Text>

                <Text
                    color="gray.600"
                    mb={3}
                    maxW="450px"
                    mx="auto"
                >
                    Enter the verification
                    code sent to your email
                    and choose a new password.
                </Text>

                {email && (
                    <Text
                        color="#2453FF"
                        fontWeight="600"
                    >
                        {email}
                    </Text>
                )}
            </Box>

            <VStack
                gap={5}
                mt={10}
            >
                {/* OTP */}

                <Box
                    w="100%"
                    display="flex"
                    justifyContent="center"
                >
                    <PinInput.Root
                        otp
                        type="numeric"
                        value={
                            otp
                                .split("")
                        }
                        onValueChange={(
                            e
                        ) =>
                            setOtp(
                                e.value.join(
                                    ""
                                )
                            )
                        }
                    >
                        <PinInput.HiddenInput />

                        <PinInput.Control>
                            <PinInput.Input
                                index={0}
                            />
                            <PinInput.Input
                                index={1}
                            />
                            <PinInput.Input
                                index={2}
                            />
                            <PinInput.Input
                                index={3}
                            />
                            <PinInput.Input
                                index={4}
                            />
                            <PinInput.Input
                                index={5}
                            />
                        </PinInput.Control>
                    </PinInput.Root>
                </Box>

                {/* NEW PASSWORD */}

                <Box w="100%">
                    <InputGroup
                        endElement={
                            <Box
                                cursor="pointer"
                                onClick={() =>
                                    setShowPassword(
                                        !showPassword
                                    )
                                }
                            >
                                {showPassword ? (
                                    <EyeOff
                                        size={18}
                                        color="#64748B"
                                    />
                                ) : (
                                    <Eye
                                        size={18}
                                        color="#64748B"
                                    />
                                )}
                            </Box>
                        }
                    >
                        <Input
                            size="lg"
                            placeholder="New Password"
                            type={
                                showPassword
                                    ? "text"
                                    : "password"
                            }
                            value={
                                newPassword
                            }
                            onChange={(
                                e
                            ) =>
                                setNewPassword(
                                    e.target
                                        .value
                                )
                            }
                        />
                    </InputGroup>

                    {/* PASSWORD REQUIREMENTS */}

                    <VStack
                        align="stretch"
                        gap={1}
                        mt={3}
                    >
                        <Text
                            fontSize="xs"
                            color="gray.500"
                        >
                            Password must contain:
                        </Text>

                        <Text
                            fontSize="xs"
                            color={
                                passwordRequirements
                                    .minLength
                                    ? "green.600"
                                    : "gray.500"
                            }
                        >
                            ✓ At least 10 characters
                        </Text>

                        <Text
                            fontSize="xs"
                            color={
                                passwordRequirements
                                    .uppercase
                                    ? "green.600"
                                    : "gray.500"
                            }
                        >
                            ✓ At least one uppercase letter
                        </Text>

                        <Text
                            fontSize="xs"
                            color={
                                passwordRequirements
                                    .lowercase
                                    ? "green.600"
                                    : "gray.500"
                            }
                        >
                            ✓ At least one lowercase letter
                        </Text>

                        <Text
                            fontSize="xs"
                            color={
                                passwordRequirements
                                    .number
                                    ? "green.600"
                                    : "gray.500"
                            }
                        >
                            ✓ At least one number
                        </Text>

                        <Text
                            fontSize="xs"
                            color={
                                passwordRequirements
                                    .special
                                    ? "green.600"
                                    : "gray.500"
                            }
                        >
                            ✓ At least one special character
                        </Text>
                    </VStack>
                </Box>

                {/* CONFIRM PASSWORD */}

                <InputGroup
                    endElement={
                        <Box
                            cursor="pointer"
                            onClick={() =>
                                setShowConfirmPassword(
                                    !showConfirmPassword
                                )
                            }
                        >
                            {showConfirmPassword ? (
                                <EyeOff
                                    size={18}
                                    color="#64748B"
                                />
                            ) : (
                                <Eye
                                    size={18}
                                    color="#64748B"
                                />
                            )}
                        </Box>
                    }
                >
                    <Input
                        size="lg"
                        placeholder="Confirm Password"
                        type={
                            showConfirmPassword
                                ? "text"
                                : "password"
                        }
                        value={
                            confirmPassword
                        }
                        onChange={(
                            e
                        ) =>
                            setConfirmPassword(
                                e.target
                                    .value
                            )
                        }
                    />
                </InputGroup>

                {confirmPassword &&
                    !passwordsMatch && (
                        <Text
                            w="100%"
                            mt={-3}
                            fontSize="xs"
                            color="red.500"
                        >
                            Passwords do not match.
                        </Text>
                    )}

                {/* SUBMIT */}

                <Button
                    w="100%"
                    size="lg"
                    bg="#2453FF"
                    color="white"
                    loading={
                        loading
                    }
                    loadingText="Resetting..."
                    disabled={
                        !isValid
                    }
                    _hover={{
                        bg: "#1B46E5",
                    }}
                    onClick={
                        handleSubmit
                    }
                >
                    Reset Password
                </Button>

                <Text
                    mt={7}
                    textAlign="center"
                    color="#2453FF"
                    cursor="pointer"
                    fontWeight="600"
                    onClick={() =>
                        navigate(
                            routes.login
                        )
                    }
                >
                    ← Back to Login
                </Text>
            </VStack>
        </Box>
    );
};

export default ResetPasswordConfirmForm;

